import { generateUploadUrl } from "@/api/upload";
import { axiosInstance } from "@/config/axios-wrapper";
import { V2Response } from "@/types/api";
import rawAxios from "axios";

export const uploadFileToS3 = async ({
  fileName,
  fileType,
  fileData,
}: {
  fileName: string;
  fileType: string;
  fileData: File;
}) => {
  try {
    // generate upload url
    const uploadedData = await generateUploadUrl(fileName);
    // upload file directly to S3 via presigned URL (use raw axios, not API client)
    const result = await rawAxios.put(uploadedData.url, fileData, {
      headers: {
        "Content-Type": fileType,
      },
    });
    const versionId = result.headers["x-amz-version-id"];
    return uploadedData.path + " " + versionId;
  } catch (err) {
    return "";
  }
};

export const validImageTypes = ["image/jpeg", "image/png", "image/bmp", "image/heif", "image/heic"];

export const validVideoTypes = [
  "video/mp4",
  "video/webm",
  "video/x-flv",
  "application/x-mpegURL",
  "video/MP2T",
  "video/3gpp",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-ms-wmv",
];

export const validDocumentTypes = ["application/pdf", "text/plain"];

// File size limits (in bytes)
export const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
export const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
export const MAX_IMAGE_COUNT = 10;
export const MAX_VIDEO_COUNT = 5;

// Validation result interface
export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validates image files for community posts
 */
export const validateImageFile = (file: File): FileValidationResult => {
  // Check file type
  if (!validImageTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid image type. Supported formats: JPEG, PNG, BMP, HEIF, HEIC. File "${file.name}" is ${file.type || 'unknown type'}.`,
    };
  }

  // Check file size
  if (file.size > MAX_IMAGE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `Image "${file.name}" is too large (${sizeMB}MB). Maximum size is 10MB.`,
    };
  }

  // Check for zero-byte files
  if (file.size === 0) {
    return {
      valid: false,
      error: `Image "${file.name}" is empty (0 bytes).`,
    };
  }

  return { valid: true };
};

/**
 * Validates video files for community posts
 */
export const validateVideoFile = (file: File): FileValidationResult => {
  // Check file type
  if (!validVideoTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid video type. Supported formats: MP4, WebM, MOV, AVI, WMV. File "${file.name}" is ${file.type || 'unknown type'}.`,
    };
  }

  // Check file size
  if (file.size > MAX_VIDEO_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `Video "${file.name}" is too large (${sizeMB}MB). Maximum size is 100MB.`,
    };
  }

  // Check for zero-byte files
  if (file.size === 0) {
    return {
      valid: false,
      error: `Video "${file.name}" is empty (0 bytes).`,
    };
  }

  return { valid: true };
};

/**
 * Validates multiple image files
 */
export const validateImageFiles = (
  files: File[],
  currentCount: number = 0
): FileValidationResult => {
  // Check total count
  const totalCount = currentCount + files.length;
  if (totalCount > MAX_IMAGE_COUNT) {
    return {
      valid: false,
      error: `You can only upload up to ${MAX_IMAGE_COUNT} images per post. You have ${currentCount} image(s) and are trying to add ${files.length} more.`,
    };
  }

  // Validate each file
  for (const file of files) {
    const result = validateImageFile(file);
    if (!result.valid) {
      return result;
    }
  }

  return { valid: true };
};

/**
 * Validates multiple video files
 */
export const validateVideoFiles = (
  files: File[],
  currentCount: number = 0
): FileValidationResult => {
  // Check total count
  const totalCount = currentCount + files.length;
  if (totalCount > MAX_VIDEO_COUNT) {
    return {
      valid: false,
      error: `You can only upload up to ${MAX_VIDEO_COUNT} videos per post. You have ${currentCount} video(s) and are trying to add ${files.length} more.`,
    };
  }

  // Validate each file
  for (const file of files) {
    const result = validateVideoFile(file);
    if (!result.valid) {
      return result;
    }
  }

  return { valid: true };
};


export const uploadFileToServer = async ({ file }: { file: File }) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axiosInstance.post<V2Response<any>>("uploads", formData);

    return response.data.result;
  } catch (err) {
    console.error("Error uploading file to server:", err);
    throw err;
  }
};

export const deleteFileFromServer = async ({ path }: { path: string }) => {
  try {
    const response = await axiosInstance.delete<V2Response<any>>("uploads", {
      data: { path },
    });
    return response.data.result;
  } catch (err) {
    console.error("Error deleting file from server:", err);
    throw err;
  }
};
